const LEADING_HASH_PATTERN = /^#/

function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') return null
  const cleaned = hex.replace(LEADING_HASH_PATTERN, '').trim()
  if (cleaned.length !== 6) return null
  const intVal = parseInt(cleaned, 16)
  if (Number.isNaN(intVal)) return null
  const r = ((intVal >> 16) & 0xff) / 255
  const g = ((intVal >> 8) & 0xff) / 255
  const b = (intVal & 0xff) / 255
  return [r, g, b]
}

function linear(c) {
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

function luminance(r, g, b) {
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b)
}

function saturation(r, g, b) {
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  return max <= 0 ? 0 : (max - min) / max
}

function parseColorSample(hex) {
  const rgb = hexToRgb(hex)
  if (!rgb) return null
  const [r, g, b] = rgb
  const lum = luminance(r, g, b)
  const sat = saturation(r, g, b)
  const cleaned = hex.replace(LEADING_HASH_PATTERN, '').trim().toUpperCase()
  return { hex: `#${cleaned}`, luminance: lum, saturation: sat }
}

/**
 * Display accent for a participant: the more saturated of primary_color / secondary_color
 * whose relative luminance is in 0.04…0.85. Falls back to pool max if neither in range.
 * Returns null if participant has no valid color.
 */
export function displayAccent(participant) {
  if (!participant?.presentation) return null
  const { primary_color, secondary_color } = participant.presentation
  const candidates = [primary_color, secondary_color]
    .filter(Boolean)
    .map(parseColorSample)
    .filter(Boolean)
  if (candidates.length === 0) return null
  const visible = candidates.filter((c) => c.luminance >= 0.04 && c.luminance <= 0.85)
  const pool = visible.length > 0 ? visible : candidates
  let best = pool[0]
  for (let i = 1; i < pool.length; i++) {
    if (pool[i].saturation > best.saturation) {
      best = pool[i]
    }
  }
  return best.hex
}

/**
 * Returns { away, home } display accents for an event's participants.
 */
export function eventTeamColors(event) {
  if (!event?.participants || event.participants.length === 0) {
    return { away: null, home: null }
  }
  let awayParticipant = event.participants.find((p) => p.role === 'away' || p.home === false)
  let homeParticipant = event.participants.find((p) => p.role === 'home' || p.home === true)

  if (!awayParticipant && !homeParticipant) {
    awayParticipant = event.participants[0]
    homeParticipant = event.participants[1]
  } else if (!awayParticipant && homeParticipant) {
    awayParticipant = event.participants.find((p) => p !== homeParticipant)
  } else if (!homeParticipant && awayParticipant) {
    homeParticipant = event.participants.find((p) => p !== awayParticipant)
  }

  return {
    away: displayAccent(awayParticipant),
    home: displayAccent(homeParticipant),
  }
}

/**
 * Checks if any open market has BET_BUILDER in tags and at least one open priced outcome.
 */
export function isSGPEligible(event) {
  if (!event?.markets) return false
  return event.markets.some((m) =>
    m.status === 'OPEN' &&
    m.metadata?.tags?.includes('BET_BUILDER') &&
    m.outcomes?.some((o) => o.status === 'OPEN' && o.odds != null)
  )
}

/**
 * Determines the tint for a Market Pulse probability slice.
 */
export function pulseTint(prob, event, index, total) {
  const home = event?.participants?.find((p) => p.role === 'home' || p.home === true)
  const away = event?.participants?.find((p) => p.role === 'away' || p.home === false)

  if (home && prob?.label === home.name) {
    return displayAccent(home) || 'var(--np-brand-secondary, var(--np-accent))'
  }
  if (away && prob?.label === away.name) {
    return displayAccent(away) || 'var(--np-accent)'
  }
  const label = prob?.label?.toLowerCase()
  if (label === 'draw' || prob?.label === 'X') {
    return 'color-mix(in srgb, var(--np-text-tertiary) 60%, transparent)'
  }
  if (total === 2) {
    return index === 0 ? 'var(--np-accent)' : 'var(--np-brand-secondary, var(--np-accent))'
  }
  return 'color-mix(in srgb, var(--np-text-tertiary) 60%, transparent)'
}

export function formatKickoffTime(iso, locale = 'en-US', timeZone) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', hour12: true, timeZone })
}

export function formatShortDate(iso, locale = 'en-US', timeZone) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone })
}
